import { Button } from "@repo/ui/components/ui/button"
import { Input } from "@repo/ui/components/ui/input"
import { DataTableFilterField } from "@repo/ui/hooks/use-data-table"
import { cn } from "@repo/ui/lib/utils"
import { Table } from "@tanstack/react-table"
import { X } from "lucide-react"
import { useMemo } from "react"
import { DateRange } from "react-day-picker"
import { useTranslations } from "use-intl"
import {
  DataTableDatePicker,
  DataTableFacetedFilter,
  DataTableVisibilityOptions,
} from "."

const options = ["visibility"] as const
type Option = (typeof options)[number]

interface DataTableToolbarProps<TData>
  extends React.HTMLAttributes<HTMLDivElement> {
  table: Table<TData>
  filterFields?: DataTableFilterField<TData>[]
  options?: Option[]
  onDateRangeChange?: (value?: DateRange) => void
  onStatusChange?: (value: string[]) => void
  showResetFilterButton?: boolean
}

function DataTableToolbar<TData>({
  table,
  children,
  className,
  onDateRangeChange,
  onStatusChange,
  options = ["visibility"],
  filterFields = [],
  showResetFilterButton = true,
  ...props
}: DataTableToolbarProps<TData>) {
  const t = useTranslations("ui.dataTable")
  const isFiltered = table.getState().columnFilters.length > 0
  const showColumnVisibilityOptions = options.includes("visibility")

  const { dateFilterColumns, searchFilterColumns, facetedFilterColumns } =
    useMemo(() => {
      const isFaceted = (
        field: DataTableFilterField<TData>,
      ): field is Extract<
        DataTableFilterField<TData>,
        { filterVariant: "faceted" }
      > => field.filterVariant === "faceted"

      return {
        dateFilterColumns: filterFields.filter(
          (f) => f.filterVariant === "date-range",
        ),
        searchFilterColumns: filterFields.filter(
          (f) => f.filterVariant === "search",
        ),
        facetedFilterColumns: filterFields.filter(isFaceted),
      }
    }, [filterFields])

  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-end justify-between gap-2 p-1",
        className,
      )}
      {...props}
    >
      <div className="flex flex-col gap-2">
        {searchFilterColumns.length > 0 &&
          searchFilterColumns.map(
            (column) =>
              table.getColumn(column.id ? String(column.id) : "") && (
                <Input
                  key={String(column.id)}
                  placeholder={column.placeholder}
                  value={
                    (table
                      .getColumn(String(column.id))
                      ?.getFilterValue() as string) ?? ""
                  }
                  onChange={(event) =>
                    table
                      .getColumn(String(column.id))
                      ?.setFilterValue(event.target.value)
                  }
                  className="h-8 w-full sm:w-[200px] lg:w-[250px] text-sm"
                />
              ),
          )}
        {dateFilterColumns.length > 0 &&
          dateFilterColumns.map((column) => (
            <DataTableDatePicker
              key={String(column.id)}
              filterField={column}
              table={table}
              onDateRangeChange={onDateRangeChange}
            />
          ))}
      </div>
      {facetedFilterColumns.length > 0 &&
        facetedFilterColumns.map((column) => (
          <DataTableFacetedFilter
            key={String(column.id)}
            column={table.getColumn(String(column.id))}
            title={column.placeholder}
            options={column.options}
            onSelect={onStatusChange}
          />
        ))}
      <div className="flex flex-1 justify-start">
        {isFiltered && showResetFilterButton && (
          <Button
            aria-label={t("resetFilters")}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => table.resetColumnFilters()}
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {children}
        {showColumnVisibilityOptions && (
          <DataTableVisibilityOptions table={table} />
        )}
      </div>
    </div>
  )
}

export default DataTableToolbar
